// The agents' stream of consciousness: wakes, System-1 gate verdicts,
// thoughts, decisions, actions and speech, newest first.
import { useState } from 'react';
import { Link } from 'wouter';
import type { Doc } from '../../convex/_generated/dataModel';
import type { AgentEventKind } from '../../convex/shared/validators';
import { fmtClock, fmtRelative, fmtUsdPrecise, shortName } from '../lib/format';
import type { Cast } from '../lib/hooks';
import { Avatar } from '../ui/Avatar';
import { Icon, type IconName } from '../ui/Icon';
import { Chip, Empty } from '../ui/primitives';

const KIND: Record<AgentEventKind, { icon: IconName; label: string; tone: string }> = {
  wake: { icon: 'sun', label: 'Woke', tone: 'sun' },
  gate: { icon: 'brain', label: 'System-1', tone: 'grape' },
  thought: { icon: 'sparkle', label: 'Thought', tone: 'sky' },
  decision: { icon: 'flag', label: 'Decision', tone: 'leaf' },
  action: { icon: 'bolt', label: 'Action', tone: 'sun' },
  speech: { icon: 'chat', label: 'Said', tone: 'sky' },
  observation: { icon: 'eye', label: 'Noticed', tone: 'neutral' },
  sleep: { icon: 'zzz', label: 'Rest', tone: 'grape' },
  error: { icon: 'alert', label: 'Error', tone: 'danger' },
  budget: { icon: 'dollar', label: 'Budget', tone: 'coral' },
};

export function FeedList({ events, cast, now, showPersona = true, emptyText = 'Nobody has had a thought yet.' }: { events: Doc<'agentEvents'>[] | undefined; cast: Cast | undefined; now: number; showPersona?: boolean; emptyText?: string }) {
  const [open, setOpen] = useState<string | null>(null);
  if (events && events.length === 0) return <Empty icon="brain" title="Quiet minds">{emptyText}</Empty>;
  return (
    <ol className="feed">
      {(events ?? []).map((e) => {
        const k = KIND[e.kind];
        const card = cast?.byId.get(e.personaId);
        const expanded = open === e._id;
        return (
          <li key={e._id} className={`feed-item kind-${e.kind}`}>
            <span className={`feed-icon tone-${k.tone}`}>
              <Icon name={k.icon} size={16} />
            </span>
            <div className="feed-body">
              <div className="feed-meta">
                {showPersona && card ? (
                  <Link href={`/agent/${card.key}`} className="feed-who">
                    <Avatar personaKey={card.key} mood={card.mood} size={20} />
                    {shortName(card.name)}
                  </Link>
                ) : null}
                <span className="feed-kind">{k.label}</span>
                <time title={new Date(e.at).toISOString()}>{fmtClock(e.at)}</time>
                <span className="feed-ago">{fmtRelative(e.at, now)}</span>
                {e.costUsd ? <Chip tone="coral">{fmtUsdPrecise(e.costUsd)}</Chip> : null}
                {e.model ? <Chip>{e.model.split('/').pop()}</Chip> : null}
              </div>
              <p className="feed-summary">{e.summary}</p>
              {e.detail ? (
                <>
                  <button type="button" className="link-btn" onClick={() => setOpen(expanded ? null : e._id)} aria-expanded={expanded}>
                    {expanded ? 'Hide details' : 'Show details'}
                  </button>
                  {expanded ? <pre className="feed-detail">{e.detail}</pre> : null}
                </>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
